import React from "react";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../../ui-kit/kuika-tooltip/kuika-tooltip";
import "./action-doc-hint.scss";

type ActionDocTooltipPosition = "top" | "right" | "bottom" | "left";

export interface IActionDocTooltipProps {
  /** The documentation text; no tooltip at all when empty. */
  content: string | undefined;
  position?: ActionDocTooltipPosition;
  contentTestId?: string;
  children: React.ReactElement;
}

/**
 * A KuikaTooltip sized for prose: action docs run to a few sentences, so the bubble wraps at a reading
 * width instead of stretching across the screen. Opens on hover and on keyboard focus of its trigger.
 */
export const ActionDocTooltip = ({
  content,
  position = "left",
  contentTestId,
  children
}: IActionDocTooltipProps): JSX.Element => (
  <KuikaTooltip
    content={content}
    position={position}
    contentClassName="kuika-action-doc-tooltip"
    contentTestId={contentTestId}
  >
    {children}
  </KuikaTooltip>
);

/** The small info glyph that marks documented text. Decorative — the trigger around it carries the label. */
export const ActionDocGlyph = (): JSX.Element => (
  <span className="kuika-action-doc-glyph" aria-hidden="true">
    <KuikaSvg name="info-circle" type="common" width={12} height={12} />
  </span>
);

export interface IActionParameterDocHintProps {
  /** The parameter's documentation; renders nothing when undefined. */
  doc: string | undefined;
  /** The parameter's visible name, for the hint's accessible label. */
  parameterLabel: string;
  contentTestId?: string;
}

/** Keeps a click on the hint from taking focus off the input the user is editing (its blur commits). */
const keepFocusWhereItIs = (event: React.MouseEvent<HTMLButtonElement>): void => {
  event.preventDefault();
};

/**
 * A focusable info glyph beside a parameter's name in the actions panel, whose tooltip explains the
 * parameter. The action picker's preview does not use it: there the whole parameter row is the tooltip
 * trigger, so it composes `ActionDocGlyph` + `ActionDocTooltip` directly — the same glyph and bubble.
 */
export const ActionParameterDocHint = ({
  doc,
  parameterLabel,
  contentTestId
}: IActionParameterDocHintProps): JSX.Element | null => {
  if (!doc) {
    return null;
  }
  return (
    <ActionDocTooltip content={doc} contentTestId={contentTestId}>
      <button
        type="button"
        className="kuika-action-doc-hint"
        aria-label={`About ${parameterLabel}`}
        onMouseDown={keepFocusWhereItIs}
      >
        <ActionDocGlyph />
      </button>
    </ActionDocTooltip>
  );
};
